// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Attachment,
    AuthorRoles,
    Dataset,
    GraphData,
    IChatMessage,
    mergeBackfilledMessages,
    UserFeedback,
} from '../../../libs/models/ChatMessage';
import { IChatUser } from '../../../libs/models/ChatUser';
import { ContentMetadataArray } from '../../../libs/models/ContentMetadata';
import { ChatState } from './ChatState';
import {
    ConversationInputChange,
    Conversations,
    ConversationsState,
    initialState,
    UpdatePluginStatePayload,
} from './ConversationsState';

export const conversationsSlice = createSlice({
    name: 'conversations',
    initialState,
    reducers: {
        setConversations: (state: ConversationsState, action: PayloadAction<Conversations>) => {
            state.conversations = action.payload;
        },
        editConversationInput: (state: ConversationsState, action: PayloadAction<ConversationInputChange>) => {
            const id = action.payload.id;
            const newInput = action.payload.newInput;
            state.conversations[id].input = newInput;
        },
        setSelectedConversation: (state: ConversationsState, action: PayloadAction<string>) => {
            state.selectedId = action.payload;
        },
        addConversation: (state: ConversationsState, action: PayloadAction<ChatState>) => {
            const newId = action.payload.id;
            state.conversations = { [newId]: action.payload, ...state.conversations };
            state.selectedId = newId;
        },
        addUserToConversation: (
            state: ConversationsState,
            action: PayloadAction<{ user: IChatUser; chatId: string }>,
        ) => {
            const { user, chatId } = action.payload;
            state.conversations[chatId].users.push(user);
            state.conversations[chatId].userDataLoaded = false;
        },
        setImportingDocumentsToConversation: (
            state: ConversationsState,
            action: PayloadAction<{ importingDocuments: string[]; chatId: string }>,
        ) => {
            const { importingDocuments, chatId } = action.payload;
            state.conversations[chatId].importingDocuments = importingDocuments;
        },
        setUsersLoaded: (state: ConversationsState, action: PayloadAction<string>) => {
            state.conversations[action.payload].userDataLoaded = true;
        },
        /*
         * addMessageToConversationFromUser() and addMessageToConversationFromServer() both update the conversations state.
         * However they are for different purposes. The former action is for updating the conversation from the
         * webapp and will be captured by the SignalR middleware and the payload will be broadcasted to all clients
         * in the same group.
         * The addMessageToConversationFromServer() action is triggered by the SignalR middleware when a response is received
         * from the webapi.
         */
        addMessageToConversationFromUser: (
            state: ConversationsState,
            action: PayloadAction<{ message: IChatMessage; chatId: string }>,
        ) => {
            const { message, chatId } = action.payload;
            updateConversation(state, chatId, message);
        },
        addMessageToConversationFromServer: (
            state: ConversationsState,
            action: PayloadAction<{ message: IChatMessage; chatId: string }>,
        ) => {
            const { message, chatId } = action.payload;
            const conversation = state.conversations[chatId];
            // Cancel-lost-the-race recovery (#1554): if we truncated a user message during
            // an attempted cancel and a bot answer is now arriving, the backend's workflow
            // beat us. Re-insert the user message so the answer isn't orphaned.
            if (conversation.cancelledUserMessage && message.userId.toLowerCase() === 'bot') {
                conversation.messages.push(conversation.cancelledUserMessage);
                conversation.cancelledUserMessage = undefined;
            }
            updateConversation(state, chatId, message);
        },
        cancelInFlightRequest: (state: ConversationsState, action: PayloadAction<{ chatId: string }>) => {
            const { chatId } = action.payload;
            const conversation = state.conversations[chatId];

            // Idempotency: the local click and the SignalR "cancelled" echo dispatch this
            // reducer back-to-back. Without this guard the second pass would truncate the
            // previous user/bot pair as well (the new "last user message") and overwrite
            // the input field with the wrong prompt.
            if (conversation.botResponseStatus === undefined) {
                return;
            }

            // Truncate messages from the last user prompt onward so both the user bubble and
            // any partial assistant bubble disappear. The prompt text is returned to the input
            // field for re-editing per issue #1554. Stash the truncated user message — if the
            // backend's workflow actually finished before our cancel landed and the bot's
            // answer arrives via SignalR, addMessageToConversationFromServer re-inserts the
            // user message so the answer isn't orphaned in history.
            // Hand-rolled reverse scan instead of Array.prototype.findLastIndex (ES2023) to keep
            // runtime compatibility with the broader production browserslist (target is ES6).
            let lastUserIdx = -1;
            for (let i = conversation.messages.length - 1; i >= 0; i--) {
                if (conversation.messages[i].authorRole === AuthorRoles.User) {
                    lastUserIdx = i;
                    break;
                }
            }
            if (lastUserIdx >= 0) {
                conversation.input = conversation.messages[lastUserIdx].content;
                conversation.cancelledUserMessage = conversation.messages[lastUserIdx];
                conversation.messages = conversation.messages.slice(0, lastUserIdx);
            } else if (typeof conversation.inFlightPrompt === 'string' && conversation.inFlightPrompt.length > 0) {
                // Race: the user clicked Stop before useChat.getResponse committed the user
                // bubble. Fall back to the prompt snapshot taken when Send was dispatched.
                conversation.input = conversation.inFlightPrompt;
            }

            conversation.disabled = false;
            conversation.botResponseStatus = undefined;
            conversation.botResponseStatusHistory = [];
            conversation.inFlightPrompt = undefined;
            conversation.activeRunId = undefined;
        },
        updateBotResponseStatus: (
            state: ConversationsState,
            action: PayloadAction<{
                chatId: string;
                status: string | null | undefined;
                statusMessage?: string | undefined;
                inFlightPrompt?: string;
                runId?: string;
            }>,
        ) => {
            const { chatId, status, inFlightPrompt, runId } = action.payload;
            const conversation = state.conversations[chatId];

            conversation.disabled = Boolean(status);

            // SignalR's terminal UI-unblock arrives as JSON null (server sends
            // `(string?)null`), but ChatInput and cancelInFlightRequest both do
            // strict-undefined checks. Normalise so the field matches its declared
            // `string | undefined` type and the stop button hides on completion (#1608).
            conversation.botResponseStatus = status ?? undefined;

            // Capture the in-flight prompt snapshot on send (status going non-undefined),
            // clear it on completion (status going undefined). cancelInFlightRequest reads
            // this when there's no user bubble in messages[] yet to restore from.
            if (status) {
                if (inFlightPrompt !== undefined) {
                    conversation.inFlightPrompt = inFlightPrompt;
                }
                if (runId !== undefined) {
                    conversation.activeRunId = runId;
                }
                // A new request is starting — drop any leftover cancelled-user-message
                // from the previous cancel so it can't be re-inserted into this new flow.
                conversation.cancelledUserMessage = undefined;
            } else {
                conversation.inFlightPrompt = undefined;
                conversation.activeRunId = undefined;
            }

            // if status is non-null, push to the end of the status array
            if (status) {
                conversation.botResponseStatusHistory.push({ status, message: action.payload.statusMessage });
            } else {
                conversation.botResponseStatusHistory = [];
            }
        },
        updateMessageProperty: <K extends keyof IChatMessage>(
            state: ConversationsState,
            action: PayloadAction<{
                property: K;
                value: IChatMessage[K];
                chatId: string;
                messageIdOrIndex: string | number;
                updatedContent?: string;
                frontLoad?: boolean;
                contentMetadataArray?: ContentMetadataArray;
                graphData?: GraphData;
                attachments?: Attachment[];
                datasets?: Dataset[];
                style?: IChatMessage['style'];
            }>,
        ) => {
            const {
                property,
                value,
                messageIdOrIndex,
                chatId,
                updatedContent,
                frontLoad,
                contentMetadataArray,
                graphData,
                attachments,
                datasets,
                style,
            } = action.payload;
            const conversation = state.conversations[chatId];
            const conversationMessage =
                typeof messageIdOrIndex === 'number'
                    ? conversation.messages[messageIdOrIndex]
                    : conversation.messages.find((m) => m.id === messageIdOrIndex);

            if (conversationMessage) {
                conversationMessage[property] = value;
                if (updatedContent) {
                    conversationMessage.content = updatedContent;
                }
                if (contentMetadataArray) {
                    // Keep raw content (with [[#n]] markers) in the store and let the render layer
                    // format it. Reformatting here baked legacy numbered <sup> into the store before
                    // ChatHistoryTextContent could turn the markers into source chips, so freshly
                    // streamed answers never showed chips with the dropdown flag on — they only
                    // appeared after a reload, when raw content came back from the server.
                    conversationMessage.contentMetadataArray = contentMetadataArray;
                }
                if (style !== undefined) {
                    // #1551 restyle pushes the new style via SignalR; keep Redux in sync so the
                    // RestyleButton flips its label immediately without waiting for a reload.
                    conversationMessage.style = style;
                }
                conversationMessage.graphData = graphData ?? conversationMessage.graphData;
                conversationMessage.datasets = datasets ?? conversationMessage.datasets;
                conversationMessage.attachments = attachments ?? conversationMessage.attachments;
            }

            if (frontLoad) {
                frontLoadChat(state, chatId);
            }
        },
        /**
         * #1641: flip the inline-CSAT collapsed flag on a specific bot message after the
         * user submits or dismisses the widget. Purely local — the server already knows
         * via /csat/submit or /csat/decline; this just keeps subsequent renders quiet.
         */
        markMessageCsatSubmitted: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; messageId: string }>,
        ) => {
            const { chatId, messageId } = action.payload;
            // The Record index signature lies about possible undefined — defensive lookup
            // matches the pattern used in signalRHubConnection.ts.
            const conversations = state.conversations as Record<string, ChatState | undefined>;
            const conversation = conversations[chatId];
            if (!conversation) return;
            const message = conversation.messages.find((m) => m.id === messageId);
            if (message) {
                message.csatSubmitted = true;
            }
        },
        /**
         * Backfills one conversation from a server snapshot after a SignalR reconnect, which drops
         * whatever the server pushed during the gap (#2175): streamed chunks of an in-flight answer,
         * or an agent's messages. Merged against the list as it is at dispatch time (see
         * mergeBackfilledMessages), so pushes that landed while the snapshot was in flight survive.
         * Guarded on the chat still being present, since a reconnect can race a delete (same reason
         * markRestyle* uses `in`).
         */
        mergeConversationMessages: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; messages: IChatMessage[] }>,
        ) => {
            const { chatId, messages } = action.payload;
            if (!(chatId in state.conversations)) {
                return;
            }
            state.conversations[chatId].messages = mergeBackfilledMessages(
                state.conversations[chatId].messages,
                messages,
            );
        },
        updatePluginState: (state: ConversationsState, action: PayloadAction<UpdatePluginStatePayload>) => {
            const { id, pluginName, newState } = action.payload;
            const isPluginEnabled = state.conversations[id].enabledHostedPlugins.find((p) => p === pluginName);
            if (newState) {
                if (isPluginEnabled) {
                    return;
                }
                state.conversations[id].enabledHostedPlugins.push(pluginName);
            } else {
                if (!isPluginEnabled) {
                    return;
                }
                state.conversations[id].enabledHostedPlugins = state.conversations[id].enabledHostedPlugins.filter(
                    (p) => p !== pluginName,
                );
            }
        },
        removeConversation: (state, action: PayloadAction<string>) => {
            const { [action.payload]: _removed, ...rest } = state.conversations;
            state.conversations = rest;
            // If the removed conversation was selected, clear the selection
            if (state.selectedId === action.payload) {
                state.selectedId = '';
            }
        },
        /**
         * Restyle button lock (#1551): mark a message as in-flight so the button is disabled
         * until SignalR delivers the update (or an error rolls it back). Scoped per-chat so a
         * restyle in chat A doesn't disable buttons in chat B.
         */
        markRestyleStarted: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; messageId: string }>,
        ) => {
            const { chatId, messageId } = action.payload;
            // The conversation may have been removed between RestyleButton's render and the
            // click handler firing (SignalR delete, another tab, etc.). Guard the same way
            // markRestyleFinished does so we don't crash on a stale chatId.
            if (!(chatId in state.conversations)) {
                return;
            }
            const conversation = state.conversations[chatId];
            const current = conversation.restylingMessageIds ?? [];
            if (!current.includes(messageId)) {
                conversation.restylingMessageIds = [...current, messageId];
            }
        },
        markRestyleFinished: (
            state: ConversationsState,
            action: PayloadAction<{ chatId: string; messageId: string }>,
        ) => {
            const { chatId, messageId } = action.payload;
            // The conversation may have been removed (user deleted/left the chat) while the
            // restyle request was still in flight — skip cleanup in that case instead of
            // throwing on a missing entry. The index-signature type hides this possibility,
            // so use `in` for a runtime check the type system doesn't object to.
            if (!(chatId in state.conversations)) {
                return;
            }
            const conversation = state.conversations[chatId];
            if (!conversation.restylingMessageIds) {
                return;
            }
            conversation.restylingMessageIds = conversation.restylingMessageIds.filter((id) => id !== messageId);
        },
    },
});

const frontLoadChat = (state: ConversationsState, id: string) => {
    const conversation = state.conversations[id];
    const { [id]: _, ...rest } = state.conversations;
    state.conversations = { [id]: conversation, ...rest };
};

const updateConversation = (state: ConversationsState, chatId: string, message: IChatMessage) => {
    const requestUserFeedback = message.userId.toLowerCase() === 'bot';
    state.conversations[chatId].messages.push({
        ...message,
        userFeedback: requestUserFeedback ? UserFeedback.Requested : undefined,
    });
    frontLoadChat(state, chatId);
};

export const {
    setConversations,
    editConversationInput,
    setSelectedConversation,
    addConversation,
    setImportingDocumentsToConversation,
    addMessageToConversationFromUser,
    addMessageToConversationFromServer,
    updateMessageProperty,
    updateBotResponseStatus,
    setUsersLoaded,
    updatePluginState,
    removeConversation,
    markRestyleStarted,
    markRestyleFinished,
    cancelInFlightRequest,
    markMessageCsatSubmitted,
    mergeConversationMessages,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;
